/**
 * Friendly error-message mapping — lives alongside StatusMessage.tsx (the
 * shared error/status display it feeds) but in its own file: a file mixing
 * a component export with a plain function trips
 * react-refresh/only-export-components, same reason auth-context.ts is
 * split out of AuthContext.tsx.
 *
 * Resolves AUD-004 / AUD-005 / AUD-006 / AUD-008 / AUD-009 / AUD-010 (Round
 * 6 polish audit, docs/AUDIT-2026-08-polish.md): a raw thrown error's own
 * message must never reach the UI — it's either a backend's internal
 * string (e.g. literally "Internal server error") or the browser's own
 * network-layer text (e.g. the literal string "Failed to fetch" —
 * AUD-005). It goes to console.error instead, for whoever's debugging in
 * devtools.
 *
 * `action` is a short lowercase phrase describing what failed, composed
 * into "Something went wrong <action>." (AUD-004's own suggested copy) —
 * e.g. friendlyErrorMessage(err, "loading your leads").
 *
 * Callers that already have a KNOWN-CODE mapping to preserve (e.g.
 * createTemplate's FORBIDDEN / TEMPLATE_LIMIT_REACHED copy) should check
 * `err.code` themselves first and only fall back to this function for the
 * unmapped/unknown case — this is just the generic unknown/5xx +
 * network-failure floor every call site shares.
 */
export function friendlyErrorMessage(err: unknown, action: string): string {
  // fetch() itself throws a plain TypeError for a real network failure
  // (offline / DNS / CORS) — as opposed to the server responding with an
  // error status, which lib/api.ts's authFetch() turns into an ApiError
  // instead. See AUD-005.
  if (err instanceof TypeError) {
    console.error(`[${action}] network failure:`, err);
    return "Can't reach the server — check your connection.";
  }
  console.error(`[${action}] failed:`, err);
  return `Something went wrong ${action}.`;
}
